import * as THREE from 'three';
import type { AbilitySlot, HeroId, HeroState, ProjectileKind, SimEvent, Simulation, Team } from '@moba/shared';
import { Music, type MusicTrack } from './Music.ts';

/**
 * Efectos de sonido sintetizados con Web Audio (no se carga ningún archivo).
 *
 * - Habilidades y combos: suenan al empezar cada acción del héroe (cambio de
 *   `actionSerial`), así funciona igual offline que con snapshots online y
 *   cubre también las habilidades que no emiten eventos (blink, escudo, agarre).
 * - Lo demás sale de los `SimEvent`: golpes, proyectiles, explosiones, muertes,
 *   estructuras, oro, niveles, tienda, recall y fin de partida.
 * - Volumen según la distancia al jugador local y paneo estéreo según la cámara.
 * - `M` silencia (se guarda en localStorage); los botones de la interfaz hacen clic.
 * - Volumen por canal (general, efectos, música, ambiente) desde Configuración.
 * - Niebla de guerra: no se oye lo que pasa fuera de la visión aliada (`audibleAt`),
 *   salvo lo que involucra al jugador.
 * - Música y ambiente: `Music.ts` (pista de menú / partida con intensidad).
 */

const STORAGE_KEY = 'moba.audio';
/** a partir de aquí el volumen empieza a bajar, y a esta distancia ya no se oye */
const NEAR = 10;
const FAR = 52;
const MAX_VOICES = 32;
const MASTER_VOLUME = 0.55;
/** la música va por debajo de los efectos aunque los dos estén al 100% */
const MUSIC_LEVEL = 0.45;
const AMBIENT_LEVEL = 0.8;

export type AudioChannel = 'master' | 'sfx' | 'music' | 'ambient';
export type AudioVolumes = Record<AudioChannel, number>;
const DEFAULT_VOLUMES: AudioVolumes = { master: 1, sfx: 1, music: 0.7, ambient: 0.7 };

type Slot = 'attack' | AbilitySlot;

interface Voice {
  /** entrada de la cadena (pan + volumen por distancia) */
  out: GainNode;
  t: number;
}

interface Sustain {
  nodes: AudioScheduledSourceNode[];
  gain: GainNode;
}

export class Sfx {
  private ctx?: AudioContext;
  private master?: GainNode;
  private sfxBus?: GainNode;
  private musicBus?: GainNode;
  private ambientBus?: GainNode;
  private music?: Music;
  private musicTrack: MusicTrack = 'menu';
  private musicIntensity = 0;
  private ambientOn = false;
  private noiseBuffer?: AudioBuffer;
  private muted = false;
  private volumes: AudioVolumes = { ...DEFAULT_VOLUMES };
  /** ¿se oye lo que pasa en (x, z)? (niebla de guerra); sin función, todo se oye */
  audibleAt?: (x: number, z: number) => boolean;
  /** se llama al cambiar el silencio o algún volumen (para refrescar la interfaz) */
  onChange?: () => void;
  /** último `attackTicks` visto de cada minion (detecta sus ataques) */
  private readonly minionAttacks = new Map<number, number>();
  private voices = 0;
  private readonly lastPlayed = new Map<string, number>();
  private readonly serials = new Map<number, number>();
  /** lanzamientos propios que ya sonaron por predicción (online), a la espera del servidor */
  private readonly predicted = new Map<number, { slot: AbilitySlot; until: number }>();
  /** sonidos largos de cada héroe (aliento, giros, recall): se cortan si cambia de acción */
  private readonly sustains = new Map<string, Sustain>();

  private listenerX = 0;
  private listenerZ = 0;
  private listenerId = 0;
  private listenerTeam: Team = 'blue';
  private rightX = Math.SQRT1_2;
  private rightZ = -Math.SQRT1_2;
  private readonly right = new THREE.Vector3();

  constructor() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as { muted?: boolean } & Partial<AudioVolumes>;
      this.muted = saved.muted === true;
      for (const channel of Object.keys(DEFAULT_VOLUMES) as AudioChannel[]) {
        const value = saved[channel];
        if (typeof value === 'number' && Number.isFinite(value)) this.volumes[channel] = Math.max(0, Math.min(1, value));
      }
    } catch {
      this.muted = false;
    }
    // el navegador sólo deja crear/reanudar el audio tras un gesto del usuario
    const unlock = (): void => this.ensureContext();
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    // clic suave en cualquier botón de la interfaz
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target as HTMLElement | null;
        const button = target?.closest('button');
        if (button && !(button as HTMLButtonElement).disabled) this.uiClick();
      },
      true,
    );
  }

  get isMuted(): boolean {
    return this.muted;
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.save();
    this.applyGains();
    this.onChange?.();
  }

  getVolumes(): AudioVolumes {
    return { ...this.volumes };
  }

  setVolume(channel: AudioChannel, value: number): void {
    this.volumes[channel] = Math.max(0, Math.min(1, value));
    this.save();
    this.applyGains();
    this.onChange?.();
  }

  resetVolumes(): void {
    this.volumes = { ...DEFAULT_VOLUMES };
    this.muted = false;
    this.save();
    this.applyGains();
    this.onChange?.();
  }

  /** Pista de música (menú / partida / ninguna) e intensidad de combate (0..1). */
  setMusic(track: MusicTrack, intensity = 0): void {
    this.musicTrack = track;
    this.musicIntensity = intensity;
    this.music?.setTrack(track);
    this.music?.setIntensity(intensity);
  }

  /** Viento y pájaros de fondo (sólo en partida). */
  setAmbient(on: boolean): void {
    this.ambientOn = on;
    this.music?.setAmbient(on);
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ muted: this.muted, ...this.volumes }));
    } catch {
      // sin almacenamiento: los ajustes duran hasta recargar
    }
  }

  private applyGains(): void {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : MASTER_VOLUME * this.volumes.master, now, 0.03);
    this.sfxBus?.gain.setTargetAtTime(this.volumes.sfx, now, 0.03);
    this.musicBus?.gain.setTargetAtTime(this.volumes.music * MUSIC_LEVEL, now, 0.03);
    this.ambientBus?.gain.setTargetAtTime(this.volumes.ambient * AMBIENT_LEVEL, now, 0.03);
  }

  /** Jugador local (distancias) y orientación de la cámara (paneo izquierda/derecha). */
  setListener(x: number, z: number, heroId: number, team: Team, camera: THREE.Camera): void {
    this.listenerX = x;
    this.listenerZ = z;
    this.listenerId = heroId;
    this.listenerTeam = team;
    this.right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    const len = Math.hypot(this.right.x, this.right.z) || 1;
    this.rightX = this.right.x / len;
    this.rightZ = this.right.z / len;
  }

  /** Nueva partida: olvida las acciones vistas y corta los sonidos largos. */
  reset(): void {
    this.serials.clear();
    this.predicted.clear();
    this.minionAttacks.clear();
    for (const key of [...this.sustains.keys()]) this.stopSustain(key);
  }

  /** Detecta el inicio de cada acción de los héroes (combo, habilidades, aturdimiento). */
  update(sim: Simulation): void {
    const now = performance.now();
    for (const hero of sim.heroes) {
      const seen = this.serials.get(hero.id);
      this.serials.set(hero.id, hero.actionSerial);
      const predicted = this.predicted.get(hero.id);
      if (seen === undefined || seen === hero.actionSerial) {
        if (predicted && now > predicted.until) {
          // el servidor no lo confirmó: se corta el sonido adelantado
          this.predicted.delete(hero.id);
          this.stopSustain(`act:${hero.id}`);
        }
        continue;
      }
      const action = hero.action;
      if (predicted) {
        this.predicted.delete(hero.id);
        // es el lanzamiento que ya sonó: no se repite ni se corta
        if (hero.alive && action.kind === 'cast' && action.slot === predicted.slot) continue;
      }
      this.stopSustain(`act:${hero.id}`);
      if (!hero.alive) continue;
      if (action.kind === 'attack') this.heroAttack(hero, action.chain);
      else if (action.kind === 'cast' && action.slot) this.heroCast(hero, action.slot, action.duration);
      else if (action.kind === 'hit') this.stunned(hero);
    }
    // minions: el contador de ataque vuelve a 0 al golpear
    const alive = new Set<number>();
    for (const minion of sim.minions) {
      if (!minion.alive) continue;
      alive.add(minion.id);
      const last = this.minionAttacks.get(minion.id);
      this.minionAttacks.set(minion.id, minion.attackTicks);
      if (last !== undefined && minion.attackTicks < last) this.minionAttack(minion.pos.x, minion.pos.z);
    }
    for (const id of this.minionAttacks.keys()) if (!alive.has(id)) this.minionAttacks.delete(id);
  }

  /** Coletazo de Koopa: golpe corto de caparazón con un chillido agudo. */
  private minionAttack(x: number, z: number): void {
    const v = this.voice(x, z, false, 0.3, 'minion-atk', 0.05);
    if (!v) return;
    this.swish(v, 2200, 900, 0.07, 0.35);
    this.tone(v, 'triangle', 620, 460, 0.06, 0.12, 0.002, v.t + 0.03);
    this.noise(v, 'bandpass', 1500, 1100, 0.04, 0.2, 3, v.t + 0.03);
  }

  play(event: SimEvent, sim: Simulation): void {
    switch (event.type) {
      case 'hit':
        this.hit(event, sim);
        break;
      case 'projectileSpawn':
        this.projectileLaunch(event.kind, event.x, event.z);
        break;
      case 'projectileExplode':
        this.explosion(event.x, event.z, event.kind === 'blackbomb');
        break;
      case 'reflect':
        this.reflect(event.x, event.z, event.heroId === this.listenerId);
        break;
      case 'death':
        this.stopSustain(`act:${event.heroId}`);
        this.stopSustain(`recall:${event.heroId}`);
        this.heroDeath(event.x, event.z, event.heroId === this.listenerId);
        break;
      case 'respawn':
        this.respawn(event.x, event.z, event.heroId === this.listenerId);
        break;
      case 'minionDeath':
        this.minionDeath(event.x, event.z);
        break;
      case 'structureDestroyed':
        this.structureDestroyed(event.x, event.z);
        break;
      case 'gold':
        if (event.heroId === this.listenerId) this.coins(event.amount >= 100 ? 3 : 1);
        break;
      case 'levelUp':
        if (event.heroId === this.listenerId) this.levelUp();
        break;
      case 'shop':
        if (event.heroId === this.listenerId) this.shop(event.action);
        break;
      case 'recall':
        this.recall(event.heroId, event.phase, event.x, event.z);
        break;
      case 'execute':
        if (event.phase === 'strike') this.swordFall(event.x, event.z, event.heroId === this.listenerId || event.targetId === this.listenerId);
        break;
      case 'matchEnd':
        this.matchEnd(event.winner === this.listenerTeam);
        break;
      default:
        break;
    }
  }

  // ------------------------------------------------------------ héroes

  private heroAttack(hero: HeroState, chain: number): void {
    const v = this.voice(hero.pos.x, hero.pos.z, hero.id === this.listenerId, 0.8, `atk:${hero.id}`, 0.05);
    if (!v) return;
    const pitch = 1 + (chain % 3) * 0.08;
    const last = chain % 3 === 2;
    switch (hero.heroId) {
      case 'knight': // tajo de espada con brillo metálico
        this.swish(v, 2600 * pitch, 650, last ? 0.2 : 0.15, 0.55);
        this.tone(v, 'sine', 3300 * pitch, 3100, 0.16, 0.05, 0.004, v.t + 0.02);
        break;
      case 'barbarian': // zarpazo pesado + gruñido
        this.swish(v, 1300 * pitch, 300, last ? 0.3 : 0.24, 0.7);
        this.growl(v, 105 * pitch, 75, 0.22, 0.22);
        break;
      case 'mage': // puñetazo de traje: barrido + blip electrónico
        this.swish(v, 2000 * pitch, 600, 0.13, 0.45);
        this.tone(v, 'square', 320 * pitch, 180, 0.07, 0.05);
        break;
      case 'falco': // el disparo suena al salir el proyectil
        break;
      case 'mario':
        this.swish(v, 1800 * pitch, 500, last ? 0.17 : 0.13, 0.55);
        if (last) this.thump(v, 160, 70, 0.1, 0.18);
        break;
      case 'sheik': // golpe rápido y ligero
        this.swish(v, 3200 * pitch, 1100, 0.09, 0.45);
        break;
    }
  }

  /** Hace sonar ya el lanzamiento propio que `HeroView.predictCast` adelanta (online). */
  predictCast(hero: HeroState, slot: AbilitySlot, durationTicks: number, windowMs: number): void {
    this.stopSustain(`act:${hero.id}`);
    this.heroCast(hero, slot, durationTicks);
    this.predicted.set(hero.id, { slot, until: performance.now() + windowMs });
  }

  private heroCast(hero: HeroState, slot: AbilitySlot, durationTicks: number): void {
    const self = hero.id === this.listenerId;
    const level = CAST_LEVELS[hero.heroId]?.[slot] ?? 1;
    const v = this.voice(hero.pos.x, hero.pos.z, self, level, `cast:${hero.id}:${slot}`, 0.1);
    if (!v) return;
    const dur = Math.max(0.2, durationTicks / 30);
    const key = `act:${hero.id}`;
    const sound = CAST_SOUNDS[hero.heroId]?.[slot];
    sound?.call(this, v, dur, key);
  }

  private stunned(hero: HeroState): void {
    const v = this.voice(hero.pos.x, hero.pos.z, hero.id === this.listenerId, 0.5, `stun:${hero.id}`, 0.3);
    if (!v) return;
    // "pajaritos": trino que baja
    for (let i = 0; i < 3; i++) this.tone(v, 'sine', 1900 - i * 160, 1500 - i * 160, 0.09, 0.08, 0.005, v.t + 0.1 + i * 0.11);
  }

  // ------------------------------------------------------------ eventos

  private hit(event: Extract<SimEvent, { type: 'hit' }>, sim: Simulation): void {
    const involves = event.sourceId === this.listenerId || event.targetId === this.listenerId;
    const targetIsStructure = sim.structures.some((s) => s.id === event.targetId);
    const sourceIsHero = sim.heroes.some((h) => h.id === event.sourceId);
    const sourceIsStructure = sim.structures.some((s) => s.id === event.sourceId);
    // los golpes son lo más frecuente: más bajos que las habilidades
    const volume = sourceIsHero ? (event.heavy ? 0.63 : 0.56) : 0.45;
    const v = this.voice(event.x, event.z, involves, volume, targetIsStructure ? 'hit:struct' : sourceIsHero ? 'hit' : 'hit:minor', 0.03);
    if (!v) return;
    if (targetIsStructure) {
      // piedra/plata: golpe metálico
      this.tone(v, 'triangle', 520, 470, 0.18, 0.28);
      this.tone(v, 'triangle', 790, 720, 0.14, 0.18);
      this.noise(v, 'highpass', 2500, 2500, 0.05, 0.25, 0.7);
      return;
    }
    if (sourceIsStructure) {
      // disparo de torre: chispazo mágico
      this.tone(v, 'sine', 1300, 500, 0.14, 0.35);
      this.noise(v, 'bandpass', 3000, 1200, 0.1, 0.3, 2);
      return;
    }
    if (event.heavy) {
      this.thump(v, 120, 38, 0.22, 0.9);
      this.noise(v, 'lowpass', 2200, 300, 0.12, 0.6, 0.8);
    } else {
      this.thump(v, 170, 60, 0.11, 0.65);
      this.noise(v, 'lowpass', 2800, 900, 0.05, 0.4, 0.8);
    }
  }

  private projectileLaunch(kind: ProjectileKind, x: number, z: number): void {
    const v = this.voice(x, z, Math.hypot(x - this.listenerX, z - this.listenerZ) < 3, kind === 'tower' ? 0.7 : 0.85, `spawn:${kind}`, 0.025);
    if (!v) return;
    switch (kind) {
      case 'arrow': // cuerda del arco
        this.tone(v, 'triangle', 240, 200, 0.3, 0.5, 0.002);
        this.tone(v, 'sine', 480, 400, 0.18, 0.18, 0.002);
        this.swish(v, 4200, 2200, 0.12, 0.3);
        break;
      case 'blaster': // "piu" láser
        this.tone(v, 'square', 1700, 280, 0.13, 0.22, 0.002);
        this.tone(v, 'sawtooth', 2400, 600, 0.08, 0.1, 0.002);
        break;
      case 'orb': // disparo de carga: carga corta + láser grave
        this.tone(v, 'sine', 300, 1300, 0.12, 0.25);
        this.tone(v, 'square', 950, 160, 0.32, 0.3, 0.004, v.t + 0.1);
        this.noise(v, 'bandpass', 1800, 500, 0.3, 0.35, 1.5, v.t + 0.1);
        break;
      case 'missile':
        this.noise(v, 'bandpass', 1400, 500, 0.55, 0.55, 1.2);
        this.tone(v, 'sawtooth', 190, 110, 0.4, 0.12);
        break;
      case 'bomb': // lanzar + mecha
      case 'blackbomb':
        this.tone(v, 'sine', 420, 220, 0.09, 0.4);
        this.noise(v, 'highpass', 5000, 6000, 0.5, 0.12, 0.7, v.t + 0.05);
        break;
      case 'needle':
        this.tone(v, 'sine', 2600, 1900, 0.09, 0.22, 0.002);
        this.swish(v, 6000, 3000, 0.07, 0.25);
        break;
      case 'fire': // bola de fuego: "fuum"
        this.noise(v, 'bandpass', 900, 400, 0.25, 0.55, 1);
        this.tone(v, 'sine', 320, 140, 0.2, 0.35);
        break;
      case 'tower': // la torre carga y suelta un rayo
        this.tone(v, 'sine', 600, 1500, 0.16, 0.3);
        this.tone(v, 'triangle', 300, 750, 0.16, 0.18);
        break;
      default:
        this.tone(v, 'sine', 800, 400, 0.1, 0.2);
    }
  }

  /** Espada gigante (R de Link): silbido de caída, choque metálico y retumbo. */
  private swordFall(x: number, z: number, self: boolean): void {
    const v = this.voice(x, z, self, 0.55, 'sword-fall', 0.1);
    if (!v) return;
    this.swish(v, 4000, 900, 0.12, 0.5);
    this.tone(v, 'square', 1250, 1180, 0.5, 0.12, 0.002, v.t + 0.1, 5000);
    this.tone(v, 'sine', 2490, 2400, 0.8, 0.1, 0.002, v.t + 0.1);
    this.thump(v, 90, 30, 0.7, 1, v.t + 0.1);
    this.noise(v, 'lowpass', 1600, 120, 0.6, 0.6, 0.7, v.t + 0.1);
  }

  private explosion(x: number, z: number, big = false): void {
    const v = this.voice(x, z, false, big ? 0.45 : 0.55, 'boom', 0.06);
    if (!v) return;
    this.noise(v, 'lowpass', big ? 3200 : 2000, 150, big ? 1.1 : 0.7, 1, 0.7);
    this.thump(v, big ? 110 : 95, 26, big ? 0.8 : 0.55, 1);
    if (big) {
      // crujido de pólvora y cascotes
      this.noise(v, 'highpass', 4000, 2500, 0.25, 0.35, 0.7, v.t + 0.02);
      for (let i = 0; i < 5; i++) this.noise(v, 'bandpass', 1500 + Math.random() * 2500, 800, 0.05, 0.18, 3, v.t + 0.2 + Math.random() * 0.5);
    }
  }

  private reflect(x: number, z: number, self: boolean): void {
    const v = this.voice(x, z, self, 0.9, 'reflect', 0.05);
    if (!v) return;
    this.tone(v, 'sine', 2500, 3100, 0.16, 0.35, 0.002);
    this.tone(v, 'triangle', 1700, 2100, 0.2, 0.2, 0.002);
  }

  private heroDeath(x: number, z: number, self: boolean): void {
    const v = this.voice(x, z, self, 0.6, 'death', 0.1);
    if (!v) return;
    this.tone(v, 'sawtooth', 440, 110, 0.65, 0.22, 0.01, v.t, 1400);
    this.tone(v, 'sine', 330, 80, 0.7, 0.35);
    this.thump(v, 110, 35, 0.4, 0.8, v.t + 0.25);
  }

  private respawn(x: number, z: number, self: boolean): void {
    const v = this.voice(x, z, self, 0.7, 'respawn', 0.1);
    if (!v) return;
    [523, 659, 784, 1046].forEach((f, i) => this.tone(v, 'triangle', f, f, 0.22, 0.22, 0.005, v.t + i * 0.07));
  }

  private minionDeath(x: number, z: number): void {
    const v = this.voice(x, z, false, 0.45, 'minion', 0.04);
    if (!v) return;
    // crujido de caparazón
    this.noise(v, 'bandpass', 1900, 900, 0.07, 0.6, 2.5);
    this.tone(v, 'sine', 300, 110, 0.12, 0.35);
  }

  private structureDestroyed(x: number, z: number): void {
    const v = this.voice(x, z, true, 0.45, 'struct', 0.3);
    if (!v) return;
    this.thump(v, 70, 22, 1.2, 1);
    this.noise(v, 'lowpass', 900, 90, 1.8, 0.9, 0.6);
    for (let i = 0; i < 7; i++) {
      const at = v.t + 0.15 + Math.random() * 1.2;
      this.noise(v, 'bandpass', 1200 + Math.random() * 1800, 600, 0.06, 0.3, 3, at);
    }
  }

  private coins(count: number): void {
    const v = this.voice(this.listenerX, this.listenerZ, true, 0.45, 'coin', 0.06);
    if (!v) return;
    for (let i = 0; i < count; i++) {
      const at = v.t + i * 0.07;
      this.tone(v, 'square', 1568, 1568, 0.06, 0.12, 0.002, at, 5000);
      this.tone(v, 'square', 2093, 2093, 0.18, 0.12, 0.002, at + 0.06, 5000);
    }
  }

  private levelUp(): void {
    const v = this.voice(this.listenerX, this.listenerZ, true, 0.7, 'level', 0.3);
    if (!v) return;
    [659, 784, 988, 1319].forEach((f, i) => {
      this.tone(v, 'triangle', f, f, 0.25, 0.3, 0.005, v.t + i * 0.08);
      this.tone(v, 'sine', f * 2, f * 2, 0.2, 0.08, 0.005, v.t + i * 0.08);
    });
  }

  private shop(action: 'buy' | 'sell'): void {
    const v = this.voice(this.listenerX, this.listenerZ, true, 0.5, 'shop', 0.08);
    if (!v) return;
    if (action === 'buy') {
      this.noise(v, 'highpass', 3000, 3000, 0.05, 0.3, 0.7);
      this.tone(v, 'square', 1319, 1319, 0.07, 0.12, 0.002, v.t + 0.03, 5000);
      this.tone(v, 'square', 1760, 1760, 0.2, 0.12, 0.002, v.t + 0.1, 5000);
    } else {
      this.tone(v, 'square', 1760, 1760, 0.07, 0.12, 0.002, v.t, 5000);
      this.tone(v, 'square', 1175, 1175, 0.18, 0.12, 0.002, v.t + 0.08, 5000);
    }
  }

  private recall(heroId: number, phase: 'start' | 'cancel' | 'done', x: number, z: number): void {
    const key = `recall:${heroId}`;
    const self = heroId === this.listenerId;
    if (phase !== 'start') this.stopSustain(key);
    // el zumbido dura 6 s: muy por debajo del resto
    const level = phase === 'start' ? (self ? 0.2 : 0.13) : self ? 0.6 : 0.4;
    const v = this.voice(x, z, self, level, `recall:${heroId}:${phase}`, 0.2);
    if (!v) return;
    if (phase === 'start') {
      // zumbido mágico que crece durante los 6 s
      const s = this.sustain(v, key, 6.2, 0.35, 2.5);
      this.sustainTone(s, v, 'sine', 220, 0.5, 5);
      this.sustainTone(s, v, 'sine', 331, 0.3, 7);
      this.sustainTone(s, v, 'triangle', 662, 0.08, 3);
    } else if (phase === 'done') {
      this.tone(v, 'sine', 300, 1600, 0.35, 0.4);
      this.noise(v, 'bandpass', 800, 4000, 0.35, 0.3, 1);
    } else {
      this.tone(v, 'sine', 500, 250, 0.15, 0.25);
    }
  }

  private matchEnd(victory: boolean): void {
    const v = this.voice(this.listenerX, this.listenerZ, true, 0.8, 'end', 1);
    if (!v) return;
    const notes = victory ? [523, 659, 784, 1046, 784, 1046] : [494, 440, 392, 330];
    const step = victory ? 0.13 : 0.24;
    notes.forEach((f, i) => {
      const last = i === notes.length - 1;
      this.tone(v, 'triangle', f, f, last ? 0.9 : step * 1.2, 0.35, 0.01, v.t + i * step);
      this.tone(v, 'square', f / 2, f / 2, last ? 0.9 : step * 1.2, 0.07, 0.01, v.t + i * step, 2400);
    });
  }

  private uiClick(): void {
    const v = this.voice(this.listenerX, this.listenerZ, true, 0.35, 'ui', 0.03);
    if (!v) return;
    this.tone(v, 'sine', 1250, 850, 0.05, 0.3, 0.002);
  }

  // ------------------------------------------------------------ síntesis

  private ensureContext(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      const compressor = this.ctx.createDynamicsCompressor();
      compressor.threshold.value = -16;
      compressor.knee.value = 12;
      compressor.ratio.value = 6;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER_VOLUME * this.volumes.master;
      this.master.connect(compressor).connect(this.ctx.destination);
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.gain.value = this.volumes.sfx;
      this.sfxBus.connect(this.master);
      this.musicBus = this.ctx.createGain();
      this.musicBus.gain.value = this.volumes.music * MUSIC_LEVEL;
      this.musicBus.connect(this.master);
      this.ambientBus = this.ctx.createGain();
      this.ambientBus.gain.value = this.volumes.ambient * AMBIENT_LEVEL;
      this.ambientBus.connect(this.master);
      const length = this.ctx.sampleRate * 2;
      this.noiseBuffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
      this.music = new Music(this.ctx, this.musicBus, this.ambientBus, this.noiseBuffer);
      this.music.setTrack(this.musicTrack);
      this.music.setIntensity(this.musicIntensity);
      this.music.setAmbient(this.ambientOn);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /**
   * Abre una voz en (x, z): volumen por distancia al jugador y paneo por cámara.
   * Devuelve null si está silenciado, no se oiría, hay demasiadas voces o el
   * mismo sonido acaba de sonar (`key` + `minGap` en segundos).
   */
  private voice(x: number, z: number, involvesPlayer: boolean, volume: number, key: string, minGap: number): Voice | null {
    const ctx = this.ctx;
    if (!ctx || !this.sfxBus || this.muted || ctx.state !== 'running') return null;
    if (this.voices >= MAX_VOICES) return null;
    // niebla de guerra: lo que no ve tu equipo no se oye (salvo si te afecta)
    if (!involvesPlayer && this.audibleAt && !this.audibleAt(x, z)) return null;
    const dx = x - this.listenerX;
    const dz = z - this.listenerZ;
    const dist = Math.hypot(dx, dz);
    let gain = volume;
    if (!involvesPlayer) {
      const f = 1 - THREE.MathUtils.clamp((dist - NEAR) / (FAR - NEAR), 0, 1);
      gain *= f * f;
    }
    if (gain < 0.02) return null;
    const now = ctx.currentTime;
    const last = this.lastPlayed.get(key);
    if (last !== undefined && now - last < minGap) return null;
    this.lastPlayed.set(key, now);

    const out = ctx.createGain();
    out.gain.value = gain;
    const panner = ctx.createStereoPanner();
    panner.pan.value = involvesPlayer && dist < 3 ? 0 : THREE.MathUtils.clamp((dx * this.rightX + dz * this.rightZ) / 22, -0.75, 0.75);
    out.connect(panner).connect(this.sfxBus);
    this.voices++;
    // la voz se libera cuando terminan sus fuentes (aprox.: a los 2.5 s o al acabar el sostenido)
    window.setTimeout(() => {
      this.voices = Math.max(0, this.voices - 1);
    }, 400);
    window.setTimeout(() => {
      out.disconnect();
      panner.disconnect();
    }, 12000);
    return { out, t: now + 0.005 };
  }

  /** Oscilador con barrido de frecuencia y envolvente percusiva. */
  private tone(
    v: Voice,
    type: OscillatorType,
    from: number,
    to: number,
    dur: number,
    gain: number,
    attack = 0.005,
    at = v.t,
    lowpass = 0,
  ): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from, at);
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(gain, at + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    let node: AudioNode = osc;
    if (lowpass > 0) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = lowpass;
      node = node.connect(filter);
    }
    node.connect(env).connect(v.out);
    osc.start(at);
    osc.stop(at + dur + 0.05);
  }

  /** Ruido filtrado con barrido del filtro. */
  private noise(
    v: Voice,
    type: BiquadFilterType,
    from: number,
    to: number,
    dur: number,
    gain: number,
    q = 1,
    at = v.t,
    attack = 0.004,
  ): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer!;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(from, at);
    if (to !== from) filter.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(gain, at + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(filter).connect(env).connect(v.out);
    src.start(at, Math.random() * 1.5);
    src.stop(at + dur + 0.05);
  }

  /** Barrido de aire (espadazo, capa, golpe). */
  private swish(v: Voice, from: number, to: number, dur: number, gain: number, at = v.t): void {
    this.noise(v, 'bandpass', from, to, dur, gain, 1.6, at, dur * 0.3);
  }

  /** Golpe grave (impacto, pisotón). */
  private thump(v: Voice, from: number, to: number, dur: number, gain: number, at = v.t): void {
    this.tone(v, 'sine', from, to, dur, gain, 0.003, at);
  }

  /** Gruñido: sierra grave filtrada con vibrato. */
  private growl(v: Voice, from: number, to: number, dur: number, gain: number, at = v.t): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(from, at);
    osc.frequency.exponentialRampToValueAtTime(to, at + dur);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 28;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = from * 0.12;
    lfo.connect(lfoGain).connect(osc.frequency);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 650;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(gain, at + 0.03);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(filter).connect(env).connect(v.out);
    osc.start(at);
    lfo.start(at);
    osc.stop(at + dur + 0.05);
    lfo.stop(at + dur + 0.05);
  }

  /** Sonido largo con entrada/salida suave; se corta con `stopSustain(key)`. */
  private sustain(v: Voice, key: string, dur: number, gain: number, fadeIn = 0.05): Sustain {
    this.stopSustain(key);
    const ctx = this.ctx!;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, v.t);
    env.gain.exponentialRampToValueAtTime(gain, v.t + fadeIn);
    env.gain.setValueAtTime(gain, v.t + Math.max(fadeIn, dur - 0.12));
    env.gain.exponentialRampToValueAtTime(0.0001, v.t + dur);
    env.connect(v.out);
    const s: Sustain = { nodes: [], gain: env };
    this.sustains.set(key, s);
    window.setTimeout(() => {
      if (this.sustains.get(key) === s) this.sustains.delete(key);
    }, dur * 1000 + 200);
    return s;
  }

  /** Ruido sostenido con el filtro modulado (fuego, viento, giro). */
  private sustainNoise(s: Sustain, v: Voice, dur: number, type: BiquadFilterType, freq: number, q: number, gain: number, lfoRate = 0, lfoDepth = 0): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer!;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(filter).connect(g).connect(s.gain);
    src.start(v.t, Math.random());
    src.stop(v.t + dur + 0.1);
    s.nodes.push(src);
    if (lfoRate > 0) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = lfoRate;
      const depth = ctx.createGain();
      depth.gain.value = lfoDepth;
      lfo.connect(depth).connect(filter.frequency);
      lfo.start(v.t);
      lfo.stop(v.t + dur + 0.1);
      s.nodes.push(lfo);
    }
  }

  /** Tono sostenido con trémolo. */
  private sustainTone(s: Sustain, v: Voice, type: OscillatorType, freq: number, gain: number, tremolo = 0, dur = 6.3, lowpass = 0): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    let node: AudioNode = osc;
    if (lowpass > 0) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = lowpass;
      node = node.connect(filter);
    }
    node.connect(g).connect(s.gain);
    osc.start(v.t);
    osc.stop(v.t + dur + 0.1);
    s.nodes.push(osc);
    if (tremolo > 0) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = tremolo;
      const depth = ctx.createGain();
      depth.gain.value = gain * 0.6;
      lfo.connect(depth).connect(g.gain);
      lfo.start(v.t);
      lfo.stop(v.t + dur + 0.1);
      s.nodes.push(lfo);
    }
  }

  private stopSustain(key: string): void {
    const s = this.sustains.get(key);
    if (!s || !this.ctx) return;
    this.sustains.delete(key);
    const now = this.ctx.currentTime;
    s.gain.gain.cancelScheduledValues(now);
    s.gain.gain.setValueAtTime(Math.max(0.0001, s.gain.gain.value), now);
    s.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    for (const node of s.nodes) {
      try {
        node.stop(now + 0.1);
      } catch {
        // ya parado
      }
    }
  }
}

type CastSound = (this: Sfx, v: Voice, dur: number, key: string) => void;

/**
 * Volumen de cada habilidad (1 = normal), medido con sonoridad ponderada K
 * renderizando cada sonido offline. Las sostenidas (giros, cargas) se quedan
 * por debajo de -36 dB porque duran segundos; los impactos grandes rondan -25.
 */
const CAST_LEVELS: Partial<Record<HeroId, Partial<Record<AbilitySlot, number>>>> = {
  knight: { w: 0.28 },
  barbarian: { e: 0.4 },
  mage: { r: 0.4 },
  falco: { e: 0.5, r: 0.3 },
  mario: { r: 0.3 },
  sheik: { e: 0.6 },
};

/**
 * Sonido al empezar cada habilidad. Los proyectiles suenan además al salir
 * (`projectileLaunch`) y los impactos con los eventos `hit`, así que aquí va
 * sólo el arranque: carga, tensado, salto, giro sostenido...
 */
const CAST_SOUNDS: Record<HeroId, Partial<Record<AbilitySlot, CastSound>>> = {
  knight: {
    // Arco: crujido de la cuerda al tensar (el disparo suena al salir la flecha)
    q(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'sawtooth', 140, 190, 0.28, 0.06, 0.05, v.t, 900);
      s.noise(v, 'bandpass', 2400, 3400, 0.25, 0.12, 6, v.t, 0.1);
    },
    // Giro de espada: viento que gira durante toda la habilidad
    w(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.55, 0.06);
      s.sustainNoise(sus, v, dur, 'bandpass', 1500, 2.2, 1, 7, 900);
      s.tone(v, 'sine', 3400, 3000, 0.25, 0.05, 0.01);
    },
    // Bomba: suena al lanzarla (proyectil)
    e(v) {
      const s = this as unknown as SfxInternals;
      s.swish(v, 1400, 600, 0.12, 0.3);
    },
    // Juicio de la Trifuerza: destello místico al invocarla (el impacto suena con el evento)
    r(v) {
      const s = this as unknown as SfxInternals;
      [659, 880, 1109, 1319].forEach((f, i) => s.tone(v, 'triangle', f, f, 0.5, 0.12, 0.01, v.t + i * 0.06));
      s.tone(v, 'sine', 2637, 2637, 0.6, 0.05, 0.05, v.t + 0.1);
      s.swish(v, 900, 3200, 0.5, 0.25, v.t + 0.1);
    },
  },
  barbarian: {
    // Aliento de fuego: crepitar de llamas sostenido (suave: dura más de 2 s)
    q(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.24, 0.2);
      s.sustainNoise(sus, v, dur, 'bandpass', 650, 0.8, 0.6, 7, 220);
      s.sustainNoise(sus, v, dur, 'lowpass', 260, 0.8, 0.5);
      s.growl(v, 95, 70, 0.45, 0.14);
    },
    // Caparazón de púas: brillo metálico
    w(v) {
      const s = this as unknown as SfxInternals;
      [1180, 1770, 2650].forEach((f, i) => s.tone(v, 'sine', f, f * 1.02, 0.6, 0.12 - i * 0.03, 0.005, v.t + i * 0.02));
      s.noise(v, 'highpass', 4000, 4000, 0.2, 0.15, 0.7);
      s.thump(v, 140, 70, 0.15, 0.3);
    },
    // Golpe de tierra: estruendo
    e(v, dur) {
      const s = this as unknown as SfxInternals;
      const at = v.t + Math.min(0.25, dur * 0.35);
      s.growl(v, 120, 85, 0.25, 0.25);
      s.thump(v, 85, 26, 0.7, 1, at);
      s.noise(v, 'lowpass', 900, 80, 0.6, 0.8, 0.7, at);
    },
    // Agarre aéreo: embestida + rugido
    r(v) {
      const s = this as unknown as SfxInternals;
      s.swish(v, 900, 300, 0.4, 0.6);
      s.growl(v, 90, 65, 0.6, 0.35);
    },
  },
  mage: {
    // Disparo de carga: zumbido de carga corto (el láser suena al salir)
    q(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'sawtooth', 120, 420, 0.22, 0.05, 0.05, v.t, 1500);
    },
    // Misil: escotilla
    w(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'square', 220, 440, 0.06, 0.08);
    },
    e(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'square', 520, 330, 0.06, 0.08);
    },
    // Screw Attack: espiral eléctrica sostenida
    r(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.5, 0.05);
      s.sustainNoise(sus, v, dur, 'bandpass', 1800, 3, 0.8, 11, 1100);
      s.sustainTone(sus, v, 'sawtooth', 210, 0.18, 22, dur, 1800);
    },
  },
  falco: {
    q() {
      // dos disparos de blaster (suenan al salir)
    },
    // Phantasm: zumbido veloz
    w(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'sawtooth', 300, 2600, 0.16, 0.12, 0.003, v.t, 6000);
      s.swish(v, 1200, 6000, 0.18, 0.5);
    },
    // Reflector: zumbido brillante con trémolo
    e(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.35, 0.02);
      s.sustainTone(sus, v, 'sine', 1400, 0.4, 30, dur);
      s.sustainTone(sus, v, 'triangle', 2100, 0.2, 0, dur);
      s.tone(v, 'sine', 900, 1800, 0.08, 0.2);
    },
    // Fire Bird: llamarada que ruge durante la carga
    r(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.7, 0.1);
      s.sustainNoise(sus, v, dur, 'bandpass', 700, 0.8, 1, 6, 400);
      s.sustainNoise(sus, v, dur, 'lowpass', 250, 0.7, 0.7);
      s.tone(v, 'sawtooth', 600, 1500, 0.3, 0.1, 0.02, v.t, 3000);
    },
  },
  mario: {
    // Capa: aleteo de tela
    q(v) {
      const s = this as unknown as SfxInternals;
      s.noise(v, 'bandpass', 1600, 450, 0.24, 0.6, 1.4, v.t, 0.04);
      s.noise(v, 'bandpass', 1200, 500, 0.12, 0.3, 1.4, v.t + 0.1, 0.02);
    },
    // Super Jump Punch: salto elástico + moneda
    w(v) {
      const s = this as unknown as SfxInternals;
      s.tone(v, 'square', 260, 900, 0.3, 0.12, 0.005, v.t, 2500);
      s.tone(v, 'square', 1046, 1046, 0.06, 0.1, 0.002, v.t + 0.1, 5000);
      s.tone(v, 'square', 1568, 1568, 0.2, 0.1, 0.002, v.t + 0.16, 5000);
    },
    e(v) {
      const s = this as unknown as SfxInternals;
      s.swish(v, 1800, 700, 0.1, 0.3);
    },
    // Tornado: viento en espiral
    r(v, dur, key) {
      const s = this as unknown as SfxInternals;
      const sus = s.sustain(v, key, dur, 0.6, 0.08);
      s.sustainNoise(sus, v, dur, 'bandpass', 1100, 2, 1, 5, 700);
      s.sustainNoise(sus, v, dur, 'bandpass', 2600, 4, 0.4, 9, 1200);
    },
  },
  sheik: {
    q(v) {
      const s = this as unknown as SfxInternals;
      s.swish(v, 5000, 2500, 0.08, 0.35);
    },
    // Vanish: estallido de humo y reaparición
    w(v, dur) {
      const s = this as unknown as SfxInternals;
      s.noise(v, 'lowpass', 2400, 300, 0.35, 0.6, 0.7);
      s.tone(v, 'sine', 400, 1300, 0.2, 0.15);
      s.noise(v, 'lowpass', 2400, 300, 0.25, 0.4, 0.7, v.t + Math.min(0.6, dur * 0.8));
    },
    // Cadena: latigazo
    e(v) {
      const s = this as unknown as SfxInternals;
      s.swish(v, 900, 3500, 0.12, 0.45);
      s.noise(v, 'highpass', 3200, 3200, 0.035, 0.9, 0.8, v.t + 0.12, 0.002);
      s.tone(v, 'triangle', 1900, 1700, 0.25, 0.08, 0.002, v.t + 0.12);
    },
    // Ráfaga: lluvia de golpes rápidos
    r(v, dur) {
      const s = this as unknown as SfxInternals;
      const n = Math.max(4, Math.min(10, Math.round(dur / 0.08)));
      for (let i = 0; i < n; i++) s.swish(v, 3400 - (i % 3) * 300, 1200, 0.07, 0.4, v.t + i * (dur / n) * 0.85);
    },
  },
};

/** Vista de los ayudantes de síntesis que usa la tabla `CAST_SOUNDS`. */
interface SfxInternals {
  tone: (v: Voice, type: OscillatorType, from: number, to: number, dur: number, gain: number, attack?: number, at?: number, lowpass?: number) => void;
  noise: (v: Voice, type: BiquadFilterType, from: number, to: number, dur: number, gain: number, q?: number, at?: number, attack?: number) => void;
  swish: (v: Voice, from: number, to: number, dur: number, gain: number, at?: number) => void;
  thump: (v: Voice, from: number, to: number, dur: number, gain: number, at?: number) => void;
  growl: (v: Voice, from: number, to: number, dur: number, gain: number, at?: number) => void;
  sustain: (v: Voice, key: string, dur: number, gain: number, fadeIn?: number) => Sustain;
  sustainNoise: (s: Sustain, v: Voice, dur: number, type: BiquadFilterType, freq: number, q: number, gain: number, lfoRate?: number, lfoDepth?: number) => void;
  sustainTone: (s: Sustain, v: Voice, type: OscillatorType, freq: number, gain: number, tremolo?: number, dur?: number, lowpass?: number) => void;
}
